import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadState, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { SubjectInfo } from "./model";

export interface SubjectListState {
    state: LoadStateInfo;
    schoolId?: string;
    list?: SubjectInfo[];
}

const initialState: SubjectListState = {
    state: notStarted(),
};

export interface RetrieveSubjectListRequest {
    schoolId: string;
}

export const retrieveSubjectList = createAsyncThunk(
    "retrieveSubjectList",
    async (param: RetrieveSubjectListRequest, thunkAPI) => {
        console.debug(`retrieve subjects for school ${param.schoolId}`);

        const response = await fetch(`${getBackendUrl()}/api/school/${param.schoolId}/subject`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (_param: RetrieveSubjectListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = (state as any).subject?.state as LoadStateInfo | undefined;
            if (status && (status.state == LoadState.Loading || status.state == LoadState.Failed)) {
                return false;
            }
            return true;
        }
    }
);

export const subjectListSlice = createSlice({
    name: "subjectList",
    initialState,
    reducers: {
        check: (state, action: PayloadAction<string>) => {
            if (state.schoolId != action.payload) {
                state.state = notStarted();
                state.schoolId = action.payload;
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSubjectList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.schoolId = action.meta.arg.schoolId;
            })
            .addCase(retrieveSubjectList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            })
            .addCase(retrieveSubjectList.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

// export const { } = subjectListSlice.actions;

export const selectSubjectList = (state: RootState) => (state as any).subjectList as SubjectListState;

export default subjectListSlice.reducer;
